import { LocalTime } from '~/components/common/LocalTime';
import { formatLocalTime } from '~/lib/datetime';
import { aiStatus } from '~/components/pages/ai/common/statusMaps';
import { Button, Modal, ModalActions, StatusChip } from '~/components/ui';
import type { PlacementVersionEntry } from '~/modules/query/ai/types';

interface PlacementVersionPreviewModalProps {
  entry: PlacementVersionEntry;
  placementName: string;
  onClose: () => void;
  onRestore: (entry: PlacementVersionEntry) => void;
}

const changedBy = (entry: PlacementVersionEntry): string =>
  entry.changedBy.displayName?.trim() || entry.changedBy.id || 'Unknown';

/** A historical version exactly as it was saved — never the current form. */
export default function PlacementVersionPreviewModal({
  entry,
  placementName,
  onClose,
  onRestore,
}: PlacementVersionPreviewModalProps) {
  return (
    <Modal
      isOpen
      onClose={onClose}
      size="large"
      title={`Version ${entry.versionNumber}`}
      description={`${placementName}, saved ${formatLocalTime(entry.createdAt)}`}
      testId="ai-placement-version-preview"
      footer={
        <ModalActions onDismiss={onClose} dismissTestId="ai-placement-version-preview-close">
          <Button
            variant="primary"
            onClick={() => onRestore(entry)}
            testId="ai-placement-version-preview-restore"
          >
            Restore version
          </Button>
        </ModalActions>
      }
    >
      <div className="space-y-5">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Version</dt>
            <dd className="font-mono text-xs text-gray-800 dark:text-gray-200">
              v{entry.versionNumber}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Status</dt>
            <dd>
              <StatusChip {...aiStatus(entry.status)} />
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Saved</dt>
            <dd className="text-gray-800 dark:text-gray-200">
              <LocalTime value={entry.createdAt} />
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Changed by</dt>
            <dd className="text-gray-800 dark:text-gray-200">{changedBy(entry)}</dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Component</dt>
            <dd className="text-gray-800 dark:text-gray-200">
              <span className="font-mono text-xs">{entry.componentId}</span> @{' '}
              {entry.componentVersion}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Model</dt>
            <dd className="text-gray-800 dark:text-gray-200">{entry.model}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-gray-500 dark:text-gray-400">Change note</dt>
            <dd className="text-gray-800 dark:text-gray-200">{entry.changeNote.trim() || '—'}</dd>
          </div>
        </dl>

        <div>
          <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            Prompt and instructions
          </p>
          <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-4 font-mono text-xs text-gray-800 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-200">
            {entry.promptTemplate || '(empty)'}
          </pre>
        </div>
      </div>
    </Modal>
  );
}
